/* import __COLOCATED_TEMPLATE__ from './grouped-messages.hbs'; */
import { on } from '@ember/object/evented';
import { scheduleOnce } from '@ember/runloop';
import { A } from '@ember/array';
import { alias, readOnly } from '@ember/object/computed';
import { observer } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@ember/component';

export default Component.extend({
  /** @param {Function} onClick */
  onProfileLinkClick: () => {},

  // Configs

  // how much time is allowed to elapse before considering a new message part of a different group
  thresholdInterval: 900000,

  // Controller/Service injection

  sortingUtils: service(),
  parsingUtils: service(),
  threadManager: service('managers/threadManager'),
  session: service(),

  currentUser: readOnly('session.currentUser'),

  // View/State Tracking

  isMobileViewport: undefined,
  wasMobileViewport: undefined,
  lastObservedThreadId: undefined,

  watchIsMobileViewport: observer('isMobileViewport', function() {
    let isMobileViewport = this.get('isMobileViewport');
    let wasMobileViewport = this.get('wasMobileViewport');

    if(isMobileViewport !== wasMobileViewport) {
      this._initializeGroups();
      this._DOM_linkifyMessages();
    }
  }),

  // Model Management

  threadModel: undefined,
  messagesList: alias('threadModel.messages'),
  sortedMessagesList: undefined,
  messagesGroupedByTimeSent: undefined,
  lastReadAt: alias('threadModel.lastReadAt'),

  watchMessagesList: observer('messagesList.[]', function() {
    if(this.get('threadModel.id') !== this.get('lastObservedThreadId')) {
      this._initializeGroups();
    }

    let messagesList = this.get('messagesList');
    let sortedMessagesList = this.get('sortedMessagesList');


    messagesList.forEach( message => {
      if(!sortedMessagesList.includes(message)) {
        this._processNewMessage(message);
      } else {
        sortedMessagesList.addObject(message);
      }
    });
  }),


  /////////////////////////////////
  //  _initializeGroups()
  //
  // * Initializes the models for the view depending upon the viewport
  /////////////////////////////////

  _initializeGroups() {
    let threadModel = this.get('threadModel');
    this.set('lastObservedThreadId', threadModel.get('id'));

    let isMobileViewport = this.get('isMobileViewport');
    this.set('wasMobileViewport', isMobileViewport);

    let sortedMessages = this._sortThreadMessages();
    this.set('sortedMessagesList', sortedMessages);

    if(!isMobileViewport) {
     return;
    }

    let thresholdInterval = this.get('thresholdInterval'); // sent-at threshold to compare messages against (in milliseconds)

    let groups = A();
    let group;

    sortedMessages.forEach(function(item) {
      if(!item.get('createdAt') || typeof item.get('createdAt').getTime !== 'function') {
        return undefined;
      }

      if(!group) {
        group = A();
        group.pushObject(item);
      } else {
        let last = group.get('lastObject');

        if(item.get('createdAt').getTime() - last.get('createdAt').getTime() < thresholdInterval) {
          group.pushObject(item);
        } else {
          groups.pushObject(group);
          group = A([item]);
        }
      }
    });

    groups.pushObject(group);

    this.set('messagesGroupedByTimeSent', groups);
  },

  /////////////////////////////////
  //  _processNewMessage(newMessage)
  //
  // * Performs gracefully modifies each model for the view without resetting
  /////////////////////////////////

  _processNewMessage(newMessage) {

    let isMobileViewport = this.get('isMobileViewport');

    if(!isMobileViewport) {
      // When adding to the desktop view's model, we want to add the object to the beginning of the array
      this.get('sortedMessagesList').unshiftObject(newMessage);
    } else {
      // We aren't explicitly using the sortedMessageList's ordering here, but we must update the model
      // with the new message anyway, so we can make a comparison for existence in watchMessagesList().
      this.get('sortedMessagesList').pushObject(newMessage);

      let thresholdInterval = this.get('thresholdInterval');
      let groups = this.get('messagesGroupedByTimeSent');
      let lastMessage = groups.get('lastObject.lastObject');

      if(newMessage.get('createdAt').getTime() - lastMessage.get('createdAt').getTime() < thresholdInterval) {
        groups.get('lastObject').pushObject(newMessage);
      } else {
        groups.pushObject(A([newMessage]));
      }
    }
  },

  /////////////////////////////////
  //  _sortThreadMessages()
  //
  // * Sorts the messagesList depending upon the view
  /////////////////////////////////

  _sortThreadMessages() {
    let sortingUtils = this.get('sortingUtils');
    let messagesList = this.get('messagesList');
    let isMobileViewport = this.get('isMobileViewport');

    let sortingCriteria = isMobileViewport ? 'createdAt:asc' : 'createdAt:desc';

    return sortingUtils.sortModel(messagesList, sortingCriteria);
  },

  /////////////////////////////////
  //  _DOM_linkifyMessages
  //
  // * Updates thread messages in the DOM by scanning for links and inserting <a> tags where appropriate
  /////////////////////////////////

  _DOM_linkifyMessages() {
    let parsingUtils = this.get('parsingUtils');

    scheduleOnce('afterRender', this, function() {
      this.element.querySelectorAll('.thread-message-body:not(.linkified) > p').forEach( function(message) {
        let textNode = message.innerHTML;
        let linkifiedText = parsingUtils.linkifyText(textNode);

        message.innerHTML = linkifiedText;
        message.classList.add('linkified');
      });
    });
  },

  /////////////////////////////////
  //  _DOM_watchMessagesList
  //
  // * Perform linkify operations on new messages
  /////////////////////////////////

  _DOM_watchMessagesList: on('init', observer('messagesList.[]', function() {
    this._DOM_linkifyMessages();
  })),

  // Initialize the component

  init() {
    this._super(...arguments);

    ['_initializeGroups',
     '_processNewMessage',
     '_sortThreadMessages',
     '_DOM_linkifyMessages',
    ].forEach(functionName => {
       this.set(functionName, this.get(functionName).bind(this));
    });

    this._initializeGroups();
  },

  actions: {
    /**
     * @param {User} user
     */
    handleProfileLinkClick(user) {
      this.get('onProfileLinkClick')(user);
    },

    markThreadReadUpToMessage(messageID) {
      const thread = this.get('threadModel');
      const user = this.get('currentUser');
      this.get('threadManager').actionMarkThreadRead(thread, user, messageID);
    },
  },
});
